import React, { useMemo } from 'react';
import styled from "styled-components";
import { MeetingRoom } from "../../models/MeetingRooms";
import { useTranslation } from "react-i18next";
import Colours from '@shared/ui/atoms/Colours';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { OfficeInformation } from '@features/desks/hooks/WithOfficeInformation';
import { PRETTY_DATE_FORMAT } from '@shared/utils/DateUtils';
import {
  MeetingRoomBookingTab,
  selectActiveBookingTab,
  selectIsSavingMeetingBooking,
  selectMeetingFromTime,
  selectMeetingToTime,
  selectOutlookBookingLink,
  selectRootOfficeForMeetingRoom,
  setActiveBookingTab
} from "../../models/meeting-room-booking-dialog.duck";
import { useDispatch, useSelector } from "react-redux";
import { largeMobile } from '@shared/ui/atoms/MediaQueries';
import { IsSuperAdmin } from '@shared/hooks/IsSuperAdmin';
import { Indicator } from '@shared/ui/atoms/Indicator';
import { HasHospitalityInMeetingBooking } from "../../models/HasHospitalityInMeetingBooking";
import { HasAttendees } from "../../models/HasAttendees";


export function MeetingRoomBookingDialogHeader(props: Props) {
  const {meetingRoom, office} = props;
  const {t} = useTranslation();
  const activeTab = useSelector(selectActiveBookingTab);
  const bookingTo = useSelector(selectMeetingToTime);
  const bookingFrom = useSelector(selectMeetingFromTime);
  const rootOffice = useSelector(selectRootOfficeForMeetingRoom);
  const outlookBookingLink = useSelector(selectOutlookBookingLink);
  const isSavingMeetingRoomBooking = useSelector(selectIsSavingMeetingBooking);
  const dispatch = useDispatch();
  const isUserSuperAdmin = IsSuperAdmin();
  const hasHospitality = HasHospitalityInMeetingBooking();
  const hasAttendees = HasAttendees();

  const timeString = useMemo(() => {
    if (bookingFrom && bookingTo) {
      return `${bookingFrom.format('HH:mm')} - ${bookingTo.format('HH:mm')}`;
    }
  }, [bookingFrom, bookingTo]);

  const dateString = useMemo(() => {
    if (bookingFrom) {
      return bookingFrom.format(PRETTY_DATE_FORMAT)
    }
  }, [bookingFrom]);

  const officeName = useMemo(() => {
    if (office.officeNamePath && office.officeNamePath.length > 0) {
      return office.officeNamePath[0];
    }
  }, [office.officeNamePath]);

  const onTabClicked = (tab: MeetingRoomBookingTab) => {
    if (!isSavingMeetingRoomBooking) {
      dispatch(setActiveBookingTab(tab));
    }
  }

  return (
    <Container>
      <Content>
        <HeadlineMedium style={{marginBottom: 8}}
                        color={Colours.white}>{meetingRoom.name} {t('settings.meeting-rooms.meeting-room')}</HeadlineMedium>
        <InfoRow>
          <InfoLeft>
            <Icon style={{marginRight: 12}} icon={IconTypes.Time} color={'white'} />
            <div>
              {timeString && <BodySmall weight={600} colour={Colours.white}>{timeString}</BodySmall>}
              {dateString && <BodySmall weight={600} colour={Colours.white}>{dateString}</BodySmall>}
              {office && <BodySmall colour={Colours.white}>{officeName} {t('settings.meeting-rooms.office')}</BodySmall>}
              {meetingRoom && <BodySmall colour={Colours.white}>{t('delete-meeting-room-dialog.capacity', {capacity: meetingRoom.capacity})}</BodySmall>}
              {outlookBookingLink && <BodyRegular><a target="_blank" style={{color: Colours.white}} href={outlookBookingLink} rel="noreferrer">{t('room-booking-dialog.outlook-link')}</a></BodyRegular>}
            </div>
          </InfoLeft>
          <InfoRight></InfoRight>
        </InfoRow>
        <TabRow>
          <TabItem isActive={activeTab === MeetingRoomBookingTab.Booking}
                   onClick={() => onTabClicked(MeetingRoomBookingTab.Booking)}>
            <BodySmall weight={600} colour={Colours.white}>Booking</BodySmall>
          </TabItem>
          {rootOffice?.hospitalityEnabled && <>
            <TabItem isActive={activeTab === MeetingRoomBookingTab.Attendees}
                     onClick={() => onTabClicked(MeetingRoomBookingTab.Attendees)}>
              <BodySmall weight={600} colour={Colours.white}>Attendees</BodySmall>
              {hasAttendees && <Indicator />}
            </TabItem>
            <TabItem isActive={activeTab === MeetingRoomBookingTab.Hospitality} onClick={() => onTabClicked(MeetingRoomBookingTab.Hospitality)}>
              <BodySmall weight={600} colour={Colours.white}>Hospitality</BodySmall>
              {hasHospitality && <Indicator />}
            </TabItem>
          </>}
          {isUserSuperAdmin && <TabItem isActive={activeTab === MeetingRoomBookingTab.Debug}
                    onClick={() => onTabClicked(MeetingRoomBookingTab.Debug)}>
            <BodySmall weight={600} colour={Colours.white}>Debug</BodySmall>
          </TabItem>}
        </TabRow>
      </Content>
    </Container>
  )
}

interface Props {
  meetingRoom: MeetingRoom;
  office: OfficeInformation;
}

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
`

const InfoLeft = styled.div`
  display: flex;
  align-items: flex-start;
  i {
    margin-top: 4px;
  }
`

const InfoRight = styled.div`
`

const TabRow = styled.div`
  display: flex;
  overflow-x: auto;
  max-width: 100%;
`

const TabItem = styled.div<any>`
  position: relative;
  border-radius: 12px 12px 0 0;
  background: ${props => props.isActive ? Colours.white: 'rgba(255, 255, 255, 0.10)'};
  display: flex;
  padding: 10px 32px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-right: 10px;
  cursor: pointer;
  user-select: none;
  &:hover {
    opacity: 0.9;
  }
  p {
    color: ${props => props.isActive ? Colours.black: Colours.white};
  }
`

const Container = styled.div`
  position: relative;
  background: linear-gradient(180deg, #27318C 0%, #4852A8 100%);
  .meetingRoomBookingDialog__close {
    position: absolute;
    top: 16px;
    right: 16px;
  }
`

const Content = styled.div`
  padding: 32px 16px 0 16px;
  @media (${largeMobile}) {
    padding: 32px 48px 0 48px;
  }
`
